import { istVeraltet, type ExamRef } from '../domain/examCatalogue';
import { examLoeschHindernis } from '../persistence/importExam';
import type { Store } from '../persistence/port';
import type { ExamRow } from '../persistence/rows';
import type { ServerClient } from './client';
import type { RelayKopf } from './dokument';
import { ladeRelayExam, PFAD } from './download';

/**
 * Was mit einer Prüfung geschah, die Relay geändert hat (`lastModifiedDate` jünger als am Gerät).
 *
 * - `neu`: ganz neu geladen — sie war hier noch nicht gestartet.
 * - `ende`: gestartet, nur das verschobene Ende übernommen.
 * - `warnen`: nichts übernommen; die Auswahl warnt weiter „Am Server geändert".
 */
export type Nachladung = 'neu' | 'ende' | 'warnen';

/** Die Prüfungen, die am Gerät liegen und von denen Relay eine jüngere Fassung hat. */
export async function veraltete(store: Store, katalog: readonly ExamRef[]): Promise<ExamRef[]> {
  const stand = new Map(
    (await store.all<Pick<ExamRow, 'id' | 'lastModifiedDate'>>('Exam')).map((e) => [e.id, e.lastModifiedDate]),
  );
  return katalog.filter((k) => stand.has(k.id) && istVeraltet(stand.get(k.id) || undefined, k.stand));
}

/**
 * Übernimmt eine Änderung von Relay nach der Regel des Nutzers (2026-10-10):
 *
 * - **Nicht gestartet** — keine Protokollzeile mit Prüfling; Gerätemeldungen ohne Prüfling
 *   zählen nicht — wird die Prüfung neu geladen. `importExam` lässt `ExamLog` stehen.
 * - **Gestartet** behält das Gerät seine Fassung. Nur ein verschobenes **Ende** ist unkritisch
 *   und wird übernommen; Beginn und Dauern bleiben, die Zeit eines Prüflings ändert sich nicht.
 *
 * Eine nicht gestartete Prüfung mit Passwort bleibt bei der Warnung: das Passwort liegt am Gerät
 * nicht vor, und ohne lehnt Relay das Dokument ab.
 *
 * ⚠️ Der Aufrufer sichert vorher, was noch ungeschrieben ist (`flushNow`) — sonst sähe eine
 * gerade begonnene Bewertung hier wie „nicht gestartet" aus.
 */
export async function uebernimmAenderung(
  client: ServerClient,
  store: Store,
  ref: Pick<ExamRef, 'id' | 'geschuetzt'>,
  hole?: (id: string) => Promise<string>,
): Promise<Nachladung> {
  const { prüflinge } = await examLoeschHindernis(store, ref.id);
  if (prüflinge === 0) {
    if (ref.geschuetzt) return 'warnen';
    await ladeRelayExam(client, store, ref.id, hole);
    return 'neu';
  }
  const antwort = await client.get<{ exams?: RelayKopf[] }>(PFAD.available);
  const kopf = (antwort?.exams ?? []).find((e) => e.id === ref.id);
  const [zeile] = await store.all<ExamRow>('Exam', { id: ref.id });
  if (!kopf || !zeile) return 'warnen';
  // Felder wie in `dokument.ts::zuBundle`. Der neue Stand ist übernommen, soweit das Gerät ihn
  // übernehmen darf — ohne ihn warnte die Auswahl für immer.
  await store.put('Exam', [{
    ...zeile,
    endDate: kopf.endDate || null,
    endTime: kopf.endTime ?? '',
    lastModifiedDate: kopf.lastModifiedDate || zeile.lastModifiedDate,
  }]);
  return 'ende';
}
